import React from 'react';
import { SkillTag, skillTagLabel } from '../../../../services/skillFrameworkService';
import { useLessonContext } from '../../LessonContext';

export function LessonSetup() {
  const { document } = useLessonContext();

  const courseContext: any = document?.course_context;
  const syllabusEntry: any = courseContext?.syllabus_entry;

  if (!courseContext || !syllabusEntry) {
    return <div className="text-sm text-gray-400">Loading lesson data...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Lesson Setup</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Pre-populated from the course syllabus. Edit as needed before generating the lesson skeleton.
          </p>
        </div>
      </div>

      {/* Course context (read-only) */}
      <div className="bg-gray-50 border border-gray-200 rounded-md px-4 py-3">
        <h4 className="text-xs font-medium text-gray-500 mb-2">Course Context</h4>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <div><span className="text-gray-500">Course:</span> <span className="text-gray-900 font-medium">{courseContext.course_title}</span></div>
          <div><span className="text-gray-500">Lesson:</span> <span className="text-gray-900 font-medium">{courseContext.lesson_number} of {courseContext.total_lessons}</span></div>
          <div><span className="text-gray-500">CEFR:</span> <span className="text-gray-900">{courseContext.targeting?.cefr_level}</span></div>
          <div><span className="text-gray-500">Audience:</span> <span className="text-gray-900">{courseContext.targeting?.audience}</span></div>
          {courseContext.previous_lesson_summary && (
            <div className="col-span-2"><span className="text-gray-500">Previous:</span> <span className="text-gray-700">{courseContext.previous_lesson_summary}</span></div>
          )}
          {courseContext.next_lesson_preview && (
            <div className="col-span-2"><span className="text-gray-500">Next:</span> <span className="text-gray-700">{courseContext.next_lesson_preview}</span></div>
          )}
        </div>
      </div>

      {/* Lesson details (from syllabus entry) */}
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
          <div className="text-sm font-medium text-gray-900">{syllabusEntry.title}</div>
        </div>
        {syllabusEntry.subtitle && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Subtitle</label>
            <div className="text-sm text-gray-700">{syllabusEntry.subtitle}</div>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Objectives</label>
          <ul className="space-y-0.5">
            {(syllabusEntry.objectives || []).map((obj: string, i: number) => (
              <li key={i} className="text-sm text-gray-700">- {obj}</li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Prerequisites</label>
            <div className="text-sm text-gray-700">{syllabusEntry.prerequisites || 'None'}</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Exit Knowledge</label>
            <div className="text-sm text-gray-700">{syllabusEntry.exit_knowledge}</div>
          </div>
        </div>

        {syllabusEntry.component_hints && syllabusEntry.component_hints.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Component Hints</label>
            <ul className="space-y-0.5">
              {syllabusEntry.component_hints.map((hint: string, i: number) => (
                <li key={i} className="text-sm text-gray-500 italic">- {hint}</li>
              ))}
            </ul>
          </div>
        )}

        {syllabusEntry.time_budget && (
          <div className="grid grid-cols-4 gap-3 text-xs">
            <div>
              <span className="text-gray-500">Reading:</span> <span className="font-medium">{syllabusEntry.time_budget.reading_minutes}m</span>
            </div>
            <div>
              <span className="text-gray-500">Exercises:</span> <span className="font-medium">{syllabusEntry.time_budget.exercises_minutes}m</span>
            </div>
            <div>
              <span className="text-gray-500">Exercise count:</span> <span className="font-medium">{syllabusEntry.time_budget.exercise_count_target}</span>
            </div>
            <div>
              <span className="text-gray-500">Total:</span> <span className="font-medium">{syllabusEntry.estimated_duration_minutes}m</span>
            </div>
          </div>
        )}

        {syllabusEntry.skill_tags && syllabusEntry.skill_tags.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Skill Tags</label>
            <div className="flex flex-wrap gap-1">
              {syllabusEntry.skill_tags.map((tag: SkillTag, i: number) => (
                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">{skillTagLabel(tag)}</span>
              ))}
            </div>
          </div>
        )}

        {syllabusEntry.targeting_context && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Targeting Context</label>
            <div className="text-sm text-gray-500 italic">{syllabusEntry.targeting_context}</div>
          </div>
        )}
      </div>

      {/* Course glossary available to this lesson */}
      {courseContext.course_glossary && courseContext.course_glossary.length > 0 && (
        <details className="border border-gray-200 rounded-md">
          <summary className="px-4 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">
            Course Glossary ({courseContext.course_glossary.length} terms available)
          </summary>
          <div className="px-4 py-2 border-t divide-y divide-gray-100">
            {courseContext.course_glossary.map((entry: any, i: number) => (
              <div key={i} className="py-1 text-xs">
                <span className="font-mono font-medium text-gray-900">{entry.term}</span>
                <span className="text-gray-400 ml-1">(L{entry.introduced_in_lesson})</span>
                <span className="text-gray-600 ml-2">{entry.canonical_short_definition}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
